import React, { Suspense, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import Dashboard from "./components/Dashboard";
import VideoPage from "./components/VideoPage";
import MainLayout from "./pages/Main";
import { ProjectProvider } from "./context/ProjectContext";
import { UserProvider, useUserContext } from "./context/UserContext";
import Login from "./pages/Auth/Login"; // Import Login Component
import Signup from "./pages/Auth/SignUp"; // Import Signup Component
import { NotificationProvider } from "./context/NotificationContext";
import ProjectDashboard from "./pages/ProjectDashboard";
import InvitationPage from "./pages/Invitation";
import AppSelection from "./pages/AppSelection";
import Cookies from "js-cookie";
import { getUserById, verifyAdmin } from "./client/api";
import CreativeRequest from "./pages/CreativeRequest";
import PromptToVideo from "./pages/PromptToVideo";
import FeedbackLoop from "./pages/FeedbackLoop";
import VideoAgent from "./pages/VideoAgent";
import VoiceDesign from "./pages/VoiceDesign";
import AvatarDesign from "./pages/AvatarDesign";
import PlanSelection from "./pages/PlanSelection";
import { AdminLayout } from "./pages/Admin/AdminLayout";
import { Box, CircularProgress } from "@mui/material";
import { AdminDashboard } from "./pages/Admin/AdminDashboard";
import { UserManagement } from "./pages/Admin/UserManagement";
import OrganizationManagement from "./pages/Admin/OrganizationManagement";
import { PremiumAnalytics } from "./pages/Admin/PremiumAnalytics";
import { FeatureAnalytics } from "./pages/Admin/FeatureAnalytics";
import { AdminProvider } from "./context/AdminContext";
import { BillingManagement } from "./pages/Admin/BillingManagement";
import { PlansManagement } from "./pages/Admin/PlansManagement";
import RequestPasswordReset from "./pages/Auth/RequestPasswordReset";
import ResetPassword from "./pages/Auth/ResetPassword";
import VerifyEmail from "./pages/Auth/VerifyEmail";
import VerifyEmailPending from "./pages/Auth/VerifyEmailPending";
import CompleteProfile from "./pages/Auth/CompleteProfile";
import AuthSuccess from "./pages/Auth/AuthSuccess";
import Mfa from "./pages/Auth/Mfa";
import Logout from "./pages/Auth/Logout";
import SilentRefresh from "./pages/Auth/SilentRefresh";
import GoogleDriveCallback from "./components/GoogleDriveCallback/GoogleDriveCallback";
import DocsPage from "./pages/Docs";

// A higher-order component to protect routes
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  adminOnly?: boolean;
}> = ({ children, adminOnly = false }) => {
  const [isVerified, setIsVerified] = useState<boolean | null>(null);
  const { user } = useUserContext();
  const authInfo = Cookies.get("authInfo");
  const userId = authInfo ? JSON.parse(authInfo).userId : null;

  useEffect(() => {
    const verify = async () => {
      if (!userId) {
        setIsVerified(false);
        return;
      }

      if (adminOnly) {
        try {
          // Call your backend verify-admin endpoint
          const isAdmin = await verifyAdmin();
          setIsVerified(isAdmin);
        } catch (error) {
          setIsVerified(false);
        }
      } else {
        // For non-admin routes, just check if user exists
        setIsVerified(true);
      }
    };

    verify();
  }, [userId, adminOnly]);

  if (isVerified === null) {
    // Loading state - you can return a spinner or null
    return <div>Loading...</div>;
  }

  if (!userId || isVerified === false) {
    return <Navigate to={adminOnly ? "/" : "/login"} replace />;
  }

  return <>{children}</>;
};

const RouteLoading = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
  >
    <CircularProgress />
  </Box>
);

function App() {
  return (
    <NotificationProvider>
      <UserProvider>
        <AdminProvider>
          <ProjectProvider>
            <Router>
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/logout" element={<Logout />} />
                <Route path="/silent-refresh" element={<SilentRefresh />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/auth/success" element={<AuthSuccess />} />
                <Route path="/mfa" element={<Mfa />} />
                <Route path="/complete-profile" element={<CompleteProfile />} />
                <Route
                  path="/google-drive-callback"
                  element={<GoogleDriveCallback />}
                />
                <Route
                  path="/request-password-reset"
                  element={<RequestPasswordReset />}
                />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/verify-email-pending" element={<VerifyEmailPending />} />
                <Route
                  path="/invitation/:workspaceId/:encodedInvitationKey"
                  element={<InvitationPage />}
                />
                <Route path="/plan-selection" element={<PlanSelection />} />
                <Route path="/docs" element={<DocsPage />} />
                {/* Protected Routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <MainLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Dashboard />} />
                  <Route
                    path="projects/:projectId/video/:videoPageId"
                    element={<VideoPage />}
                  />
                  <Route
                    path="projects/:projectId"
                    element={<ProjectDashboard />}
                  />
                  <Route path="prompt-to-video" element={<PromptToVideo />} />
                  <Route path="feedback-loop" element={<FeedbackLoop />} />
                  <Route path="video-agent" element={<VideoAgent />} />
                  <Route path="voice-design" element={<VoiceDesign />} />
                  <Route path="avatar-design" element={<AvatarDesign />} />
                  <Route
                    path="/creative-request"
                    element={
                      <ProtectedRoute>
                        <CreativeRequest />
                      </ProtectedRoute>
                    }
                  />
                </Route>
                {/* Admin Routes */}
                <Route
                  path="/admin-dashboard"
                  element={
                    <ProtectedRoute adminOnly>
                      <React.Suspense fallback={<RouteLoading />}>
                        <AdminLayout />
                      </React.Suspense>
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<AdminDashboard />} />
                  <Route path="users" element={<UserManagement />} />
                  <Route
                    path="organizations"
                    element={<OrganizationManagement />}
                  />
                  <Route path="billing" element={<BillingManagement />} />
                  <Route path="plans" element={<PlansManagement />} />
                  <Route path="analytics" element={<PremiumAnalytics />} />
                  <Route path="feature-analytics" element={<FeatureAnalytics />} />
                </Route>
                <Route path="/app-selection" element={<AppSelection />} />
              </Routes>
            </Router>
          </ProjectProvider>
        </AdminProvider>
      </UserProvider>
    </NotificationProvider>
  );
}

export default App;
